import { Link } from "react-router-dom";
import InitialsAvatar from "@/components/base/InitialsAvatar";
import { usePageMeta } from "@/hooks/usePageMeta";

const services = [
  { icon: "ri-newspaper-line", title: "影视资讯", desc: "第一时间跟进院线与平台新片动态、行业消息、奖项与幕后揭秘。" },
  { icon: "ri-file-text-line", title: "剧情介绍", desc: "整理完整剧情脉络与作品背景，帮你快速判断是否符合口味。" },
  { icon: "ri-group-line", title: "演员阵容", desc: "导演与主演资料、角色设定一目了然，追星与选片都方便。" },
  { icon: "ri-book-open-line", title: "深度影评", desc: "邀请影评人撰写有观点的分析，提供买票前值得信任的参考。" },
];

const team = [
  { name: "陆时", role: "主编 · 影评人", bio: "十年影视媒体从业经验，专注华语类型片与作者电影。" },
  { name: "江浸月", role: "资深编辑", bio: "负责剧集专题与深度访谈，偏爱科幻与现实主义题材。" },
  { name: "苏黎", role: "内容编辑", bio: "负责资讯快讯与作品资料整理，关注演员与幕后故事。" },
];

const values = [
  { icon: "ri-shield-check-line", title: "信息可核", desc: "资料以公开信息为基础整理，标注来源逻辑，不臆测、不夸大。" },
  { icon: "ri-lightbulb-line", title: "观点清晰", desc: "影评有立场也有依据，允许不同声音，但拒绝无意义的情绪输出。" },
  { icon: "ri-heart-3-line", title: "读者优先", desc: "所有内容围绕「帮助你看懂、选对」展开，而非流量与噱头。" },
];

export default function About() {
  usePageMeta(
    "关于我们 - 疯狂的欲望影视资讯站",
    "了解疯狂的欲望：一个专注最新电影电视剧资讯、剧情介绍、演员阵容与深度影评的影视内容门户。",
  );

  return (
    <div className="w-full">
      <section className="bg-primary-950 text-background-50">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-14 md:px-8 md:py-20">
          <nav className="mb-5 flex items-center gap-2 text-xs text-background-300">
            <Link to="/" className="cursor-pointer hover:text-background-50">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-100">关于我们</span>
          </nav>
          <span className="inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-2.5 py-1 text-xs font-bold text-primary-950">
            <i className="ri-information-line" />
            关于疯狂的欲望
          </span>
          <h1 className="mt-4 max-w-3xl font-heading text-3xl font-black leading-tight md:text-5xl">
            让每一次观影决定，都更有底气
          </h1>
          <p className="mt-5 max-w-3xl text-sm leading-relaxed text-background-200 md:text-base">
            「疯狂的欲望」是一个以影视内容为核心的资讯门户。我们聚焦最新电影与电视剧，提供资讯、剧情介绍、演员阵容与专业影评，
            目标是让每一位影视爱好者和观影决策者，都能在这里用最少的时间，获得最值得信赖的判断依据。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1400px] px-4 py-14 md:px-8 md:py-20">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">内容服务范围</h2>
        <p className="mt-2 max-w-2xl text-sm text-foreground-600">从「发现」到「了解」，再到「判断」，我们覆盖观影决策的完整链路。</p>
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((s) => (
            <div key={s.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className={`${s.icon} text-2xl`} />
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-14 md:px-8 md:py-20">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">我们的坚持</h2>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {values.map((v) => (
              <div key={v.title} className="rounded-lg bg-background-50 p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${v.icon} text-2xl`} />
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1400px] px-4 py-14 md:px-8 md:py-20">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">编辑团队</h2>
        <p className="mt-2 text-sm text-foreground-600">一群真正热爱影视、也愿意认真写字的人。</p>
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-3">
          {team.map((m) => (
            <div key={m.name} className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-6">
              <InitialsAvatar name={m.name} size={56} />
              <div className="min-w-0">
                <h3 className="font-heading text-lg font-bold text-foreground-950">{m.name}</h3>
                <p className="text-xs font-medium text-accent-700">{m.role}</p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{m.bio}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-14 md:px-8 md:py-16" id="disclaimer">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">版权声明与免责声明</h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-600">
            <p>本站所有影视作品名称、剧情简介、演员阵容与影评内容，均为演示用途的虚构示例，不指向任何真实作品、机构或个人，仅供产品功能展示。</p>
            <p>本站不提供任何影片播放、下载、会员或付费功能，不存储任何受版权保护的影视资源。</p>
            <p>如站内演示内容与真实作品存在名称巧合，纯属无意，可联系我们进行修改或删除。</p>
          </div>
          <Link
            to="/contact"
            className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
          >
            <i className="ri-mail-send-line" />
            联系我们
          </Link>
        </div>
      </section>
    </div>
  );
}